import { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { GrantCard } from "@/components/GrantCard";
import { GrantDetailsModal } from "@/components/GrantDetailsModal";
import { ComparisonModal } from "@/components/ComparisonModal";
import { HistoryModal } from "@/components/HistoryModal";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  ArrowLeft, Sparkles, Search, Loader2, Building2, RefreshCw, SearchX, GitCompare, X, Download, History, Mail,
} from "lucide-react";

export default function SearchGrants() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [company, setCompany] = useState(null);
  const [bandi, setBandi] = useState([]);
  const [savedTitles, setSavedTitles] = useState(new Set());
  const [feedbackMap, setFeedbackMap] = useState({});
  const [searching, setSearching] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [lastAt, setLastAt] = useState(null);

  const [q, setQ] = useState("");
  const [tipo, setTipo] = useState("all");
  const [stato, setStato] = useState("all");

  const loadFavorites = useCallback(async () => {
    const { data } = await api.get(`/favorites?company_id=${id}`);
    setSavedTitles(new Set(data.map((f) => f.grant?.titolo)));
  }, [id]);

  const loadFeedback = useCallback(async () => {
    try {
      const { data } = await api.get(`/companies/${id}/feedback`);
      setFeedbackMap(data || {});
    } catch {
      setFeedbackMap({});
    }
  }, [id]);

  useEffect(() => {
    (async () => {
      try {
        const [c, ls] = await Promise.all([
          api.get(`/companies/${id}`),
          api.get(`/companies/${id}/last-search`),
        ]);
        setCompany(c.data);
        setBandi(ls.data.bandi || []);
        setLastAt(ls.data.last_search_at);
        await loadFavorites();
        await loadFeedback();
      } catch {
        toast.error("Azienda non trovata");
        navigate("/app");
      } finally {
        setLoaded(true);
      }
    })();
  }, [id, navigate, loadFavorites, loadFeedback]);

  const submitFeedback = async (grant, value) => {
    setFeedbackMap((prev) => {
      const next = { ...prev };
      if (value) next[grant.titolo] = value;
      else delete next[grant.titolo];
      return next;
    });
    if (!value) return;
    try {
      await api.post("/feedback", { company_id: id, grant_title: grant.titolo, feedback: value });
      toast.success("Grazie per la segnalazione");
    } catch {
      toast.error("Segnalazione non riuscita");
      loadFeedback();
    }
  };

  const runSearch = async () => {
    setSearching(true);
    try {
      const { data } = await api.post(`/companies/${id}/search`);
      setBandi(data.bandi || []);
      setLastAt(new Date().toISOString());
      toast.success(`${data.bandi?.length || 0} bandi trovati dall'AI`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Errore durante la ricerca");
    } finally {
      setSearching(false);
    }
  };

  const toggleFavorite = async (grant) => {
    const isSaved = savedTitles.has(grant.titolo);
    try {
      if (isSaved) {
        const { data } = await api.get(`/favorites?company_id=${id}`);
        const fav = data.find((f) => f.grant?.titolo === grant.titolo);
        if (fav) await api.delete(`/favorites/${fav.id}`);
        toast.success("Rimosso dai preferiti");
      } else {
        await api.post("/favorites", { company_id: id, grant });
        toast.success("Salvato nei preferiti");
      }
      await loadFavorites();
    } catch {
      toast.error("Operazione non riuscita");
    }
  };

  const [selected, setSelected] = useState(null);
  const [compareIds, setCompareIds] = useState([]);
  const [compareOpen, setCompareOpen] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [history, setHistory] = useState([]);

  const openHistory = async () => {
    try {
      const { data } = await api.get(`/companies/${id}/history`);
      setHistory(data);
    } catch {
      setHistory([]);
    }
    setHistoryOpen(true);
  };

  const downloadReport = async () => {
    setDownloading(true);
    try {
      const res = await api.get(`/companies/${id}/report`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `bandi_${(company?.ragione_sociale || "azienda").replace(/[^A-Za-z0-9]+/g, "_")}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Report PDF scaricato");
    } catch {
      toast.error("Impossibile generare il report");
    } finally {
      setDownloading(false);
    }
  };

  const [downloadingXlsx, setDownloadingXlsx] = useState(false);
  const [sendOpen, setSendOpen] = useState(false);
  const [sendEmail, setSendEmail] = useState("");
  const [sendName, setSendName] = useState("");
  const [sending, setSending] = useState(false);

  const sendReport = async () => {
    if (!sendEmail) return;
    setSending(true);
    try {
      const { data } = await api.post(`/companies/${id}/send-report`, { email: sendEmail, client_name: sendName });
      toast.success(`Scheda inviata a ${data.email} (${data.inviati} bandi)`);
      setSendOpen(false);
      setSendEmail("");
      setSendName("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Invio non riuscito");
    } finally {
      setSending(false);
    }
  };

  const downloadExcel = async () => {
    setDownloadingXlsx(true);
    try {
      const res = await api.get(`/companies/${id}/report.xlsx`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `bandi_${(company?.ragione_sociale || "azienda").replace(/[^A-Za-z0-9]+/g, "_")}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("File Excel scaricato");
    } catch {
      toast.error("Impossibile generare il file Excel");
    } finally {
      setDownloadingXlsx(false);
    }
  };

  const toggleCompare = (grant) => {
    setCompareIds((prev) => {
      if (prev.includes(grant.id)) return prev.filter((x) => x !== grant.id);
      if (prev.length >= 3) {
        toast.info("Puoi confrontare al massimo 3 bandi");
        return prev;
      }
      return [...prev, grant.id];
    });
  };

  const filtered = useMemo(() => {
    return bandi.filter((b) => {
      if (q && !`${b.titolo} ${b.sottotitolo} ${b.ente}`.toLowerCase().includes(q.toLowerCase())) return false;
      if (tipo !== "all" && b.tipologia !== tipo) return false;
      if (stato !== "all" && b.stato !== stato) return false;
      return true;
    });
  }, [bandi, q, tipo, stato]);

  if (!loaded) {
    return <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;
  }

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
      <button onClick={() => navigate("/app")} className="mb-4 flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-slate-900" data-testid="back-button">
        <ArrowLeft className="h-4 w-4" /> Torna alle aziende
      </button>

      <div className="flex flex-col justify-between gap-4 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:flex-row sm:items-center">
        <div className="flex items-center gap-3">
          <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-indigo-50 text-indigo-600">
            <Building2 className="h-6 w-6" />
          </span>
          <div>
            <h1 className="font-display text-xl font-bold tracking-tight text-slate-900">{company?.ragione_sociale}</h1>
            <p className="text-sm text-slate-500">
              {company?.ateco} · {company?.regione} · {company?.dimensione}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Button onClick={openHistory} variant="outline" size="lg" className="gap-2" data-testid="open-history-button">
            <History className="h-5 w-5" />
            Storico
          </Button>
          {bandi.length > 0 && (
            <Button onClick={downloadReport} disabled={downloading} variant="outline" size="lg" className="gap-2" data-testid="download-report-button">
              {downloading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Download className="h-5 w-5" />}
              Scarica PDF
            </Button>
          )}
          {bandi.length > 0 && (
            <Button onClick={downloadExcel} disabled={downloadingXlsx} variant="outline" size="lg" className="gap-2" data-testid="download-excel-button">
              {downloadingXlsx ? <Loader2 className="h-5 w-5 animate-spin" /> : <Download className="h-5 w-5" />}
              Esporta Excel
            </Button>
          )}
          {bandi.length > 0 && (
            <Button onClick={() => setSendOpen(true)} variant="outline" size="lg" className="gap-2" data-testid="send-report-button">
              <Mail className="h-5 w-5" />
              Invia al cliente
            </Button>
          )}
          <Button onClick={runSearch} disabled={searching} className="gap-2" size="lg" data-testid="run-ai-search-button">
            {searching ? <Loader2 className="h-5 w-5 animate-spin" /> : bandi.length ? <RefreshCw className="h-5 w-5" /> : <Sparkles className="h-5 w-5" />}
            {searching ? "Ricerca in corso..." : bandi.length ? "Aggiorna ricerca" : "Cerca bandi con AI"}
          </Button>
        </div>
      </div>

      {searching && (
        <div className="mt-6 grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3">
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="scan-shimmer relative h-64 overflow-hidden rounded-2xl border border-slate-200 bg-white p-5">
              <div className="h-4 w-24 rounded bg-slate-100" />
              <div className="mt-4 h-5 w-3/4 rounded bg-slate-100" />
              <div className="mt-2 h-4 w-1/2 rounded bg-slate-100" />
              <div className="mt-6 h-8 w-full rounded bg-slate-100" />
            </div>
          ))}
        </div>
      )}

      {!searching && bandi.length === 0 && (
        <div className="mt-8 flex flex-col items-center justify-center rounded-2xl border border-dashed border-slate-300 bg-white py-20 text-center" data-testid="no-search-yet">
          <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-indigo-50">
            <Sparkles className="h-7 w-7 text-indigo-500" />
          </span>
          <h3 className="mt-4 font-display text-lg font-bold text-slate-900">Avvia la ricerca AI</h3>
          <p className="mt-1 max-w-sm text-sm text-slate-500">
            L'intelligenza artificiale analizzerà il profilo di questa azienda e troverà i bandi
            più compatibili con punteggio dettagliato.
          </p>
          <Button onClick={runSearch} className="mt-6 gap-2"><Sparkles className="h-4 w-4" /> Cerca bandi con AI</Button>
        </div>
      )}

      {!searching && bandi.length > 0 && (
        <>
          <div className="mt-6 flex flex-col gap-3 rounded-2xl border border-slate-200 bg-white p-4 sm:flex-row sm:items-center">
            <div className="relative flex-1">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <Input data-testid="grant-search-input" value={q} onChange={(e) => setQ(e.target.value)}
                placeholder="Cerca per parola chiave..." className="pl-9" />
            </div>
            <Select value={tipo} onValueChange={setTipo}>
              <SelectTrigger className="sm:w-52" data-testid="grant-funding-type-filter"><SelectValue placeholder="Tipologia" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutte le tipologie</SelectItem>
                <SelectItem value="Fondo Perduto">Fondo Perduto</SelectItem>
                <SelectItem value="Credito d'Imposta">Credito d'Imposta</SelectItem>
                <SelectItem value="Tasso Agevolato">Tasso Agevolato</SelectItem>
                <SelectItem value="Misto">Misto</SelectItem>
              </SelectContent>
            </Select>
            <Select value={stato} onValueChange={setStato}>
              <SelectTrigger className="sm:w-44" data-testid="grant-status-filter"><SelectValue placeholder="Stato" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti gli stati</SelectItem>
                <SelectItem value="Aperto">Aperto</SelectItem>
                <SelectItem value="In Arrivo">In Arrivo</SelectItem>
                <SelectItem value="In Scadenza">In Scadenza</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <p className="mt-4 text-sm text-slate-500" data-testid="results-count">
            <span className="font-semibold text-slate-900">{filtered.length}</span> bandi ·
            {" "}suggerimenti AI, verifica i dettagli sui portali ufficiali
          </p>

          {filtered.length === 0 ? (
            <div className="mt-6 flex flex-col items-center justify-center rounded-2xl border border-dashed border-slate-300 py-16 text-center text-slate-500">
              <SearchX className="h-8 w-8 text-slate-400" />
              <p className="mt-2">Nessun bando corrisponde ai filtri selezionati.</p>
            </div>
          ) : (
            <div className="mt-4 grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3">
              {filtered.map((g) => (
                <GrantCard key={g.id} grant={g} saved={savedTitles.has(g.titolo)}
                  onToggleFavorite={toggleFavorite} onOpen={setSelected}
                  comparing={compareIds.includes(g.id)} onToggleCompare={toggleCompare}
                  compareDisabled={compareIds.length >= 3}
                  feedback={feedbackMap[g.titolo]} onFeedback={submitFeedback} />
              ))}
            </div>
          )}
        </>
      )}

      {compareIds.length > 0 && (
        <div className="fixed inset-x-0 bottom-4 z-40 mx-auto flex w-fit items-center gap-3 rounded-full border border-slate-200 bg-white px-4 py-2.5 shadow-lg" data-testid="compare-bar">
          <span className="flex items-center gap-1.5 text-sm font-semibold text-slate-700">
            <GitCompare className="h-4 w-4 text-indigo-600" />
            {compareIds.length} selezionat{compareIds.length > 1 ? "i" : "o"}
          </span>
          <Button size="sm" onClick={() => setCompareOpen(true)} disabled={compareIds.length < 2} data-testid="open-comparison-button">
            Confronta
          </Button>
          <button onClick={() => setCompareIds([])} className="rounded-full p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700" data-testid="clear-comparison-button">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      <GrantDetailsModal grant={selected} open={Boolean(selected)} onOpenChange={(o) => !o && setSelected(null)} />
      <ComparisonModal
        grants={bandi.filter((b) => compareIds.includes(b.id))}
        open={compareOpen}
        onOpenChange={setCompareOpen}
      />
      <HistoryModal entries={history} open={historyOpen} onOpenChange={setHistoryOpen} />

      <Dialog open={sendOpen} onOpenChange={setSendOpen}>
        <DialogContent data-testid="send-report-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl font-bold">Invia scheda al cliente</DialogTitle>
            <DialogDescription>
              Invieremo via email i 6 bandi più compatibili di {company?.ragione_sociale} con fonte e scadenza.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="send-email">Email destinatario</Label>
              <Input id="send-email" type="email" value={sendEmail} onChange={(e) => setSendEmail(e.target.value)}
                placeholder="cliente@azienda.it" data-testid="send-report-email-input" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="send-name">Nome cliente (facoltativo)</Label>
              <Input id="send-name" value={sendName} onChange={(e) => setSendName(e.target.value)}
                placeholder="Es. Dott. Rossi" data-testid="send-report-name-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSendOpen(false)}>Annulla</Button>
            <Button onClick={sendReport} disabled={sending || !sendEmail} className="gap-2" data-testid="send-report-confirm-button">
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
              Invia scheda
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
